import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WHATSAPP_CONFIG } from '../config/whatsapp';

const CART_STORAGE_KEY = 'babilonia-cart';

export interface Product {
  id: number;
  name: string;
  price: number; // Mantener para compatibilidad pero no se usa actualmente
  image: string;
  category: string;
}

export interface CartItem extends Product {
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  sendToWhatsApp: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

// Función para cargar el carrito desde localStorage
const loadCartFromStorage = (): CartItem[] => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
    return [];
  }
};

// Función para guardar el carrito en localStorage
const saveCartToStorage = (items: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(loadCartFromStorage);

  // Sincronizar con localStorage cada vez que cambian los items
  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);

  const addToCart = (product: Product) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === product.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevItems, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: number) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const sendToWhatsApp = () => {
    if (items.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    const { phoneNumber, greeting } = WHATSAPP_CONFIG;
    
    // Construir el mensaje
    let message = `${greeting}\n\n`;
    message += '*Productos:*\n';
    
    items.forEach((item, index) => {
      message += `\n${index + 1}. *${item.name}*\n`;
      message += `   • Cantidad: ${item.quantity}\n`;
      // DESHABILITADO: Precio en mensaje de WhatsApp
      // message += `   • Precio unitario: $${item.price.toFixed(2)}\n`;
      // message += `   • Subtotal: $${(item.price * item.quantity).toFixed(2)}\n`;
    });
    
    message += `\n━━━━━━━━━━━━━━━━━━━\n`;
    message += `*Total de productos:* ${totalItems}\n`;
    // DESHABILITADO: Total a pagar
    // message += `*Total a pagar:* $${totalPrice.toFixed(2)}\n`;
    message += `\n¡Gracias! 😊`;

    // Codificar el mensaje para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Crear el enlace de WhatsApp
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Abrir WhatsApp en una nueva ventana
    window.open(whatsappUrl, '_blank');
  };

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        sendToWhatsApp,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
